import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '../../components/ui/Input';
import { PaginaCrud, type CampoFormulario } from '../../components/layout/PaginaCrud';
import type { FilaInventario } from '../../../data/repositories/RepositorioInventario';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { aNumero, aTexto } from '../../../data/mappers/conversiones';
import estilos from '../../components/layout/PaginaCrud.module.css';

interface Area {
  id: number;
  nombre: string;
  descripcion: string | null;
  clienteId: number | null;
  clienteNombre: string;
}

export function Areas() {
  const [clienteId, setClienteId] = useState<number | null>(null);

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const nombresCliente = useMemo(() => {
    const mapa = new Map<number, string>();
    for (const c of clientes.data ?? []) mapa.set(c.id, c.nombre);
    return mapa;
  }, [clientes.data]);

  const mapear = useCallback(
    (fila: FilaInventario): Area => {
      const clienteId = aNumero(fila.cliente_id);

      return {
        id: aNumero(fila.id) ?? 0,
        nombre: aTexto(fila.nombre) ?? 'Sin nombre',
        descripcion: aTexto(fila.descripcion),
        clienteId,
        clienteNombre:
          clienteId !== null ? (nombresCliente.get(clienteId) ?? `Cliente ${clienteId}`) : '—',
      };
    },
    [nombresCliente],
  );

  const campos: CampoFormulario[] = [
    {
      clave: 'nombre',
      etiqueta: 'Nombre',
      tipo: 'texto',
      requerido: true,
      placeholder: 'Planta baja, Almacén, Sala técnica…',
    },
    {
      clave: 'cliente_id',
      etiqueta: 'Cliente',
      tipo: 'select',
      requerido: true,
      placeholder: 'Elige un cliente',
      ayuda: 'A qué empresa pertenece esta zona',
      opciones: (clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre })),
    },
    {
      clave: 'descripcion',
      etiqueta: 'Descripción',
      tipo: 'texto',
      placeholder: 'Opcional',
    },
  ];

  return (
    <PaginaCrud<Area>
      entidad="areas"
      titulo="Áreas"
      descripcion="Zonas en las que se divide la instalación de cada cliente."
      textoVacio="Crea un área para poder colocar nodos dentro de ella."
      mapear={mapear}
      claveFila={(a) => a.id}
      textoBusqueda={(a) => `${a.nombre} ${a.clienteNombre} ${a.descripcion ?? ''}`}
      idDeFila={(fila) => aNumero(fila.id) ?? 0}
      filtrosConsulta={{ cliente_id: clienteId }}
      filtrosExtra={
        <Select
          etiqueta="Cliente"
          placeholder="Todos los clientes"
          value={clienteId ?? ''}
          onChange={(e) => setClienteId(e.target.value ? Number(e.target.value) : null)}
          opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
        />
      }
      campos={campos}
      aFormulario={(fila) => ({
        nombre: aTexto(fila.nombre) ?? '',
        cliente_id: aNumero(fila.cliente_id) ?? '',
        descripcion: aTexto(fila.descripcion) ?? '',
      })}
      aRegistro={(v) => ({
        nombre: String(v.nombre ?? '').trim(),
        cliente_id: Number(v.cliente_id),
        descripcion: String(v.descripcion ?? '').trim() || null,
      })}
      textoBorrado={(a) =>
        `¿Eliminar el área «${a.nombre}»?\n\n` +
        'Los nodos y sensores que estén dentro se eliminarán con ella.'
      }
      columnas={[
        {
          clave: 'nombre',
          encabezado: 'Área',
          movil: 'titulo',
          render: (a) => <span className={estilos.nombre}>{a.nombre}</span>,
        },
        {
          clave: 'cliente',
          encabezado: 'Cliente',
          movil: 'subtitulo',
          render: (a) => a.clienteNombre,
        },
        {
          clave: 'descripcion',
          encabezado: 'Descripción',
          render: (a) => <span className={estilos.secundario}>{a.descripcion ?? '—'}</span>,
        },
      ]}
    />
  );
}
